// JS-only image compositor. v1 supports source-over (alpha compositing).
// Other blend modes can be added by extending `blendPixel` — the surrounding
// loop and alpha math stay identical.
//
// Why JS instead of canvas globalCompositeOperation: filters need to read the
// running composite as input, and `getImageData` after `drawImage` flushes
// the GPU pipeline. Doing everything on ImageData keeps the path predictable
// and avoids the readback stall for filter inputs. Cost: ~30ms for 8 layers
// at 256×256 — acceptable for an editor; export goes through the same path.

import type { BlendMode } from './types';

export function emptyImageData(w: number, h: number): ImageData {
  return new ImageData(w, h);
}

export function cloneImageData(src: ImageData): ImageData {
  return new ImageData(new Uint8ClampedArray(src.data), src.width, src.height);
}

/**
 * Composite `src` onto `base` (or transparent if base is null).
 * Returns a NEW ImageData. Does not mutate inputs.
 */
export function composite(
  base: ImageData | null,
  src: ImageData,
  mode: BlendMode,
  alpha: number,
  w: number,
  h: number,
): ImageData {
  if (alpha === 0) return base !== null ? cloneImageData(base) : emptyImageData(w, h);
  const out = base !== null ? cloneImageData(base) : emptyImageData(w, h);
  blendInto(out, src, mode, alpha);
  return out;
}

function blendInto(dest: ImageData, src: ImageData, mode: BlendMode, alpha: number): void {
  if (dest.width !== src.width || dest.height !== src.height) {
    throw new Error(
      `composite: size mismatch ${dest.width}×${dest.height} vs ${src.width}×${src.height}`,
    );
  }
  // For now: source-over only. Other blend modes will fork inside the inner loop.
  if (mode !== 'source-over') {
    // Future: switch on mode and apply per-channel formula before alpha mix.
    // Keep behaviour identical to source-over for unsupported modes in v1.
  }
  const d = dest.data;
  const s = src.data;
  const len = d.length;
  for (let i = 0; i < len; i += 4) {
    const sa = ((s[i + 3] as number) / 255) * alpha;
    if (sa === 0) continue;
    if (sa === 1) {
      d[i] = s[i] as number;
      d[i + 1] = s[i + 1] as number;
      d[i + 2] = s[i + 2] as number;
      d[i + 3] = 255;
      continue;
    }
    const dAlpha = (d[i + 3] as number) / 255;
    const dContrib = dAlpha * (1 - sa);
    const aOut = sa + dContrib;
    if (aOut === 0) continue;
    d[i]     = ((s[i]     as number) * sa + (d[i]     as number) * dContrib) / aOut;
    d[i + 1] = ((s[i + 1] as number) * sa + (d[i + 1] as number) * dContrib) / aOut;
    d[i + 2] = ((s[i + 2] as number) * sa + (d[i + 2] as number) * dContrib) / aOut;
    d[i + 3] = aOut * 255;
  }
}
